import { Link } from 'react-router-dom';
import SectionCard from '../../components/SectionCard.jsx';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import StatusWord from '../../components/StatusWord.jsx';
import { customerSectionPath } from '../../lib/navigation.js';
import { CAMPAIGN_TONE, RESULT_TONE, periodWords } from '../../lib/access-review-format.js';
import { AC67_WORDS, STATE_WORDS } from '../../../../shared/access-reviews.mjs';

/** The Reports row of a closed campaign's locked report (the Reports tab expands `?report=<id>`). */
export const reportPath = (customerId, reportId) => `${customerSectionPath(customerId, 'reports')}?report=${encodeURIComponent(reportId)}`;

/**
 * "Campaigns" (spec 13.1, item 7): Name, Period, State, Decided, Removals, Evidence. A closed row links to its locked
 * report in Reports, where the auditor reads it too. `reportsLinked` false (the viewer, who has no Reports page) shows
 * the words without a link.
 */
export default function CampaignsTable({ campaigns, customerId, reportsLinked = true }) {
  const columns = [
    { id: 'name', header: 'Name', sortValue: (c) => c.openedAt, cell: (c) => <span className="font-semibold">{c.name}</span> },
    { id: 'period', header: 'Period', hideBelow: 'md', sortValue: (c) => c.openedAt, cell: (c) => periodWords(c.openedAt, c.dueAt) },
    { id: 'state', header: 'State', sortValue: (c) => STATE_WORDS[c.state], cell: (c) => <StatusWord tone={CAMPAIGN_TONE[c.state] ?? 'off'}>{STATE_WORDS[c.state] ?? c.state}</StatusWord> },
    { id: 'decided', header: 'Decided', hideBelow: 'sm', cell: (c) => (c.state === 'scheduled' ? 'None yet' : `${c.decided} of ${c.total}`) },
    { id: 'removals', header: 'Removals', hideBelow: 'md', cell: (c) => (c.state === 'scheduled' ? 'None yet' : `${c.removalsDecided} (${c.removalsVerified} verified)`) },
    { id: 'evidence', header: 'Evidence', cell: (c) => <Evidence campaign={c} customerId={customerId} linked={reportsLinked} /> },
  ];
  return (
    <SectionCard title="Campaigns" description="Each closed campaign has a locked results report in your evidence bucket, and a verification addendum when it had removals to verify" testId="access-reviews-campaigns">
      <ConsoleTable
        label="Campaigns" columns={columns} rows={campaigns} getRowId={(c) => c.campaignId} rowName={(c) => c.name}
        defaultSort={{ columnId: 'name', direction: 'descending' }} pageSizes={[10, 25]} framed={false}
        emptyState={{ title: 'No campaign yet', body: 'Your first campaign appears here when it starts.' }}
      />
    </SectionCard>
  );
}

const ADDENDUM_WORDS = Object.freeze({ pending: 'Verification addendum: pending', retrying: 'Verification addendum: failed, retrying', failed: 'Verification addendum: failed' });

/** The verification addendum: a link once ready, else its state in words (never silent when it failed). */
function Addendum({ campaign: c, customerId, linked }) {
  const state = c.addendum ?? (c.verificationReportId ? 'ready' : c.state === 'closing' && c.reportId ? 'pending' : null);
  if (!state) return null;
  if (state === 'ready') {
    return linked
      ? <Link className="font-semibold text-ipc-light hover:underline" to={reportPath(customerId, c.verificationReportId)} data-testid="access-reviews-addendum-link">Verification addendum: ready</Link>
      : <span data-testid="access-reviews-addendum-state">Verification addendum: ready, in Reports</span>;
  }
  return <span className={state === 'pending' ? 'text-ipc-muted' : 'text-ipc-bad'} data-testid="access-reviews-addendum-state">{ADDENDUM_WORDS[state]}</span>;
}

/**
 * The evidence of a campaign (product rule 2026-10-30): the results report as soon as it exists (a closing campaign
 * included), then the verification addendum when the removals are verified or the window ended. A campaign closed with
 * one single report shows "Report".
 */
function Evidence({ campaign: c, customerId, linked }) {
  if (c.state === 'cancelled') return <span className="text-ipc-muted">None, cancelled</span>;
  if (c.state !== 'closed' && !(c.state === 'closing' && c.reportId)) return <span className="text-ipc-muted">{c.state === 'closing' ? 'Results report being created' : 'After close'}</span>;
  const result = c.state === 'closed' && c.result ? <StatusWord tone={RESULT_TONE[c.result] ?? 'off'} strong={false}>AC-6(7) {AC67_WORDS[c.result] ?? c.result}</StatusWord> : null;
  if (!c.reportId) return <span className="inline-flex flex-wrap items-center gap-x-3">{result}<span className="text-ipc-muted">Report being created</span></span>;
  const split = c.state === 'closing' || Boolean(c.verificationReportId);
  const label = split ? 'Results report' : 'Report';
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3">
      {result}
      {linked ? <Link className="font-semibold text-ipc-light hover:underline" to={reportPath(customerId, c.reportId)} data-testid="access-reviews-report-link">{label}</Link> : <span>{label} in Reports</span>}
      <Addendum campaign={c} customerId={customerId} linked={linked} />
    </span>
  );
}
